package redmcrosco.alex.farmatch.ui

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.PopupProperties
import kotlinx.datetime.*
import farmatch.composeapp.generated.resources.*
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import redmcrosco.alex.farmatch.models.Farmacia
import redmcrosco.alex.farmatch.models.Farmacista
import redmcrosco.alex.farmatch.models.UserRole
import redmcrosco.alex.farmatch.utils.ItalianCfUtils
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun RegisterScreen(
    viewModel: AuthViewModel,
    onBackToLogin: () -> Unit,
    onSettingsClick: () -> Unit = {},
    currentLanguage: String = "it",
    onLanguageChange: (String) -> Unit = {}
) {
    var role by remember { mutableStateOf(UserRole.FARMACISTA) }
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    
    // Campi Farmacista
    var nome by remember { mutableStateOf("") }
    var cognome by remember { mutableStateOf("") }
    var dataNascita by remember { mutableStateOf("") }
    var luogoNascita by remember { mutableStateOf("") }
    var sesso by remember { mutableStateOf("M") }
    var cittadinanza by remember { mutableStateOf("Italiana") }
    var codiceFiscale by remember { mutableStateOf("") }
    var numeroIscrizioneOrdine by remember { mutableStateOf("") }

    // Campi Fiscali Farmacista
    var regimeFiscale by remember { mutableStateOf("FORFETTARIO") }
    var percEnpaf by remember { mutableStateOf("2.0") }
    
    // Competenze
    val competenzePredefinite = listOf("Elettrocardiogramma", "Tamponi", "Galenica", "Deblistering", "Gestione amministrativa", "Cosmesi", "Nutrizione", "Vaccinazione", "Omeopatia")
    val selectedSkills = remember { mutableStateListOf<String>() }
    var showOtherSkillField by remember { mutableStateOf(false) }
    var otherSkillText by remember { mutableStateOf("") }
    var expandedCompetenze by remember { mutableStateOf(false) }
    
    // Campi Farmacia
    var nomeFarmacia by remember { mutableStateOf("") }
    var ragioneSociale by remember { mutableStateOf("") }
    var partitaIva by remember { mutableStateOf("") }
    var codiceSDI by remember { mutableStateOf("") }
    var pec by remember { mutableStateOf("") }
    var numeroIscrizioneOrdineDirettore by remember { mutableStateOf("") }
    var indirizzo by remember { mutableStateOf("") }
    var cap by remember { mutableStateOf("") }
    var citta by remember { mutableStateOf("") }
    var provincia by remember { mutableStateOf("") }
    var regione by remember { mutableStateOf("") }
    var telefono by remember { mutableStateOf("") }

    // Stati per i menu a tendina
    var expRegione by remember { mutableStateOf(false) }
    var expProvincia by remember { mutableStateOf(false) }
    var expComune by remember { mutableStateOf(false) }
    var expCap by remember { mutableStateOf(false) }

    var suggerimentiComuni by remember { mutableStateOf<List<String>>(emptyList()) }
    var showSuggerimenti by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    val scrollState = rememberScrollState()
    var errorMsgLocal by remember { mutableStateOf<String?>(null) }
    
    // Stati per il DatePicker
    var showDatePicker by remember { mutableStateOf(false) }
    val datePickerState = rememberDatePickerState()
    
    val taxCodeErrorLength = stringResource(Res.string.tax_code_error_length)
    val taxCodeErrorInvalid = stringResource(Res.string.tax_code_error_invalid)

    // Validazione base per abilitare il tasto
    val isFormValid = if (role == UserRole.FARMACISTA) {
        nome.isNotBlank() && cognome.isNotBlank() && dataNascita.isNotBlank() && luogoNascita.isNotBlank() && cittadinanza.isNotBlank() && email.contains("@") && password.length >= 6 && codiceFiscale.length == 16 && regione.isNotBlank()
    } else {
        nomeFarmacia.isNotBlank() && partitaIva.isNotBlank() && indirizzo.isNotBlank() && cap.isNotBlank() && citta.isNotBlank() && provincia.isNotBlank() && regione.isNotBlank() && email.contains("@") && password.length >= 6
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(24.dp)
            .statusBarsPadding()
            .verticalScroll(scrollState),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Icona Impostazioni (Ingranaggio) in alto a destra - Apre direttamente il dettaglio
        Box(modifier = Modifier.fillMaxWidth()) {
            IconButton(
                onClick = onSettingsClick,
                modifier = Modifier.align(Alignment.CenterEnd)
            ) {
                Icon(
                    imageVector = AppIcons.Settings,
                    contentDescription = "Settings",
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(28.dp)
                )
            }
        }
        Image(
            painter = painterResource(Res.drawable.farmatch_logo_00),
            contentDescription = "Farmatch Logo",
            modifier = Modifier.height(50.dp).padding(bottom = 16.dp)
        )

        Text(stringResource(Res.string.register_title_screen), style = MaterialTheme.typography.headlineMedium, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary)
        
        Spacer(modifier = Modifier.height(24.dp))
        
        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
            RadioButton(selected = role == UserRole.FARMACISTA, onClick = { role = UserRole.FARMACISTA })
            Text(stringResource(Res.string.farmacista))
            Spacer(modifier = Modifier.width(16.dp))
            RadioButton(selected = role == UserRole.FARMACIA, onClick = { role = UserRole.FARMACIA })
            Text(stringResource(Res.string.farmacia))
        }

        Spacer(modifier = Modifier.height(16.dp))

        if (role == UserRole.FARMACISTA) {
            OutlinedTextField(value = nome, onValueChange = { nome = it }, label = { Text(stringResource(Res.string.name_label) + " *") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp))
            Spacer(modifier = Modifier.height(8.dp))
            OutlinedTextField(value = cognome, onValueChange = { cognome = it }, label = { Text(stringResource(Res.string.surname_label) + " *") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp))
            Spacer(modifier = Modifier.height(8.dp))
            
            Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Text(stringResource(Res.string.gender_label), style = MaterialTheme.typography.bodyMedium)
                RadioButton(selected = sesso == "M", onClick = { sesso = "M" })
                Text(stringResource(Res.string.male_gender))
                Spacer(modifier = Modifier.width(16.dp))
                RadioButton(selected = sesso == "F", onClick = { sesso = "F" })
                Text(stringResource(Res.string.female_gender))
            }
            Spacer(modifier = Modifier.height(8.dp))

            Spacer(modifier = Modifier.height(8.dp))

            if (showDatePicker) {
                DatePickerDialog(
                    onDismissRequest = { showDatePicker = false },
                    confirmButton = {
                        TextButton(onClick = {
                            datePickerState.selectedDateMillis?.let { millis ->
                                val date = Instant.fromEpochMilliseconds(millis).toLocalDateTime(TimeZone.UTC).date
                                dataNascita = "${date.dayOfMonth.toString().padStart(2, '0')}/${date.monthNumber.toString().padStart(2, '0')}/${date.year}"
                            }
                            showDatePicker = false
                        }) {
                            Text(stringResource(Res.string.ok_button))
                        }
                    },
                    dismissButton = {
                        TextButton(onClick = { showDatePicker = false }) {
                            Text(stringResource(Res.string.cancel_button))
                        }
                    }
                ) {
                    DatePicker(state = datePickerState)
                }
            }

            OutlinedTextField(
                value = dataNascita, 
                onValueChange = { dataNascita = it }, 
                label = { Text(stringResource(Res.string.birth_date_label)) }, 
                modifier = Modifier.fillMaxWidth(), 
                shape = RoundedCornerShape(12.dp),
                trailingIcon = {
                    IconButton(onClick = { showDatePicker = true }) {
                        Icon(Icons.Default.DateRange, contentDescription = "Select Date")
                    }
                }
            )
            Spacer(modifier = Modifier.height(8.dp))
            
            Box(modifier = Modifier.fillMaxWidth()) {
                OutlinedTextField(
                    value = luogoNascita,
                    onValueChange = { 
                        luogoNascita = it
                        if (it.length >= 3) {
                            scope.launch {
                                suggerimentiComuni = viewModel.searchComuni(it)
                                showSuggerimenti = suggerimentiComuni.isNotEmpty()
                            }
                        } else {
                            showSuggerimenti = false
                        }
                    },
                    label = { Text(stringResource(Res.string.birth_place_label)) },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp)
                )
                if (showSuggerimenti && suggerimentiComuni.isNotEmpty()) {
                    DropdownMenu(expanded = showSuggerimenti, onDismissRequest = { showSuggerimenti = false }, properties = PopupProperties(focusable = false)) {
                        suggerimentiComuni.forEach { comuneStr ->
                            DropdownMenuItem(
                                text = { Text(comuneStr) }, 
                                onClick = { luogoNascita = comuneStr; showSuggerimenti = false }
                            )
                        }
                    }
                }
            }
            Spacer(modifier = Modifier.height(8.dp))
            OutlinedTextField(value = cittadinanza, onValueChange = { cittadinanza = it }, label = { Text(stringResource(Res.string.citizenship_label)) }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp))
            Spacer(modifier = Modifier.height(8.dp))
            OutlinedTextField(value = codiceFiscale, onValueChange = { codiceFiscale = it }, label = { Text(stringResource(Res.string.tax_code_label)) }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp))
            Spacer(modifier = Modifier.height(8.dp))
            OutlinedTextField(value = numeroIscrizioneOrdine, onValueChange = { numeroIscrizioneOrdine = it }, label = { Text(stringResource(Res.string.order_registration_label)) }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp))
            Spacer(modifier = Modifier.height(16.dp))

            // --- DATI FISCALI ---
            Text("Dati Fiscali (Opzionali)", style = MaterialTheme.typography.titleSmall, color = MaterialTheme.colorScheme.primary, fontWeight = FontWeight.Bold, modifier = Modifier.align(Alignment.Start))
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                EditableDropdownField(
                    label = "Regime Fiscale",
                    value = regimeFiscale,
                    onValueChange = { regimeFiscale = it },
                    options = listOf("FORFETTARIO", "ORDINARIO", "MINIMI"),
                    modifier = Modifier.weight(1f)
                )
                OutlinedTextField(
                    value = percEnpaf,
                    onValueChange = { percEnpaf = it },
                    label = { Text("% ENPAF") },
                    modifier = Modifier.weight(0.5f),
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                    shape = RoundedCornerShape(12.dp)
                )
            }
            Spacer(modifier = Modifier.height(16.dp))

            // --- SEZIONE COMPETENZE ---
            Text("Competenze Professionali", style = MaterialTheme.typography.titleSmall, color = MaterialTheme.colorScheme.primary, fontWeight = FontWeight.Bold, modifier = Modifier.align(Alignment.Start))
            Spacer(modifier = Modifier.height(8.dp))
            
            ExposedDropdownMenuBox(
                expanded = expandedCompetenze,
                onExpandedChange = { expandedCompetenze = !expandedCompetenze },
                modifier = Modifier.fillMaxWidth()
            ) {
                OutlinedTextField(
                    value = if (selectedSkills.isEmpty()) "Seleziona competenze..." else "${selectedSkills.size} selezionate",
                    onValueChange = {},
                    readOnly = true,
                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expandedCompetenze) },
                    modifier = Modifier.menuAnchor().fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp)
                )
                ExposedDropdownMenu(expanded = expandedCompetenze, onDismissRequest = { expandedCompetenze = false }) {
                    competenzePredefinite.forEach { skill ->
                        val isSelected = selectedSkills.contains(skill)
                        DropdownMenuItem(
                            text = {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Checkbox(checked = isSelected, onCheckedChange = null)
                                    Spacer(Modifier.width(8.dp))
                                    Text(skill)
                                }
                            },
                            onClick = {
                                if (isSelected) selectedSkills.remove(skill) else selectedSkills.add(skill)
                            }
                        )
                    }
                    DropdownMenuItem(
                        text = { 
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(18.dp))
                                Spacer(Modifier.width(8.dp))
                                Text("Altro...") 
                            }
                        },
                        onClick = { showOtherSkillField = !showOtherSkillField; expandedCompetenze = false }
                    )
                }
            }

            if (showOtherSkillField) {
                Spacer(modifier = Modifier.height(8.dp))
                Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                    OutlinedTextField(
                        value = otherSkillText,
                        onValueChange = { otherSkillText = it },
                        label = { Text("Specifica altra competenza") },
                        modifier = Modifier.weight(1f),
                        shape = RoundedCornerShape(12.dp),
                        singleLine = true
                    )
                    IconButton(onClick = {
                        if (otherSkillText.isNotBlank()) {
                            if (!selectedSkills.contains(otherSkillText.trim())) {
                                selectedSkills.add(otherSkillText.trim())
                            }
                            otherSkillText = ""
                            showOtherSkillField = false
                        }
                    }) {
                        Icon(Icons.Default.Check, contentDescription = "Aggiungi", tint = MaterialTheme.colorScheme.primary)
                    }
                }
            }

            if (selectedSkills.isNotEmpty()) {
                Spacer(modifier = Modifier.height(8.dp))
                FlowRow(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    selectedSkills.forEach { skill ->
                        InputChip(
                            selected = true,
                            onClick = { selectedSkills.remove(skill) },
                            label = { Text(skill, fontSize = 11.sp) },
                            trailingIcon = { Icon(Icons.Default.Close, contentDescription = null, modifier = Modifier.size(14.dp)) }
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.height(16.dp))
            Text(
                text = stringResource(Res.string.operating_area_title),
                style = MaterialTheme.typography.titleSmall,
                color = MaterialTheme.colorScheme.primary,
                fontWeight = FontWeight.Bold,
                modifier = Modifier.align(Alignment.Start)
            )
            Spacer(modifier = Modifier.height(8.dp))

            // REGIONE OPERATIVITA
            ExposedDropdownMenuBox(
                expanded = expRegione,
                onExpandedChange = { 
                    expRegione = !expRegione 
                    if (expRegione && viewModel.listaRegioni.isEmpty()) {
                        viewModel.loadRegioni()
                    }
                },
                modifier = Modifier.fillMaxWidth()
            ) {
                OutlinedTextField(
                    value = regione,
                    onValueChange = {},
                    readOnly = true,
                    label = { Text(stringResource(Res.string.region_label)) },
                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expRegione) },
                    modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp)
                )
                ExposedDropdownMenu(expanded = expRegione, onDismissRequest = { expRegione = false }) {
                    viewModel.listaRegioni.forEach { r ->
                        DropdownMenuItem(
                            text = { Text(r) },
                            onClick = {
                                regione = r
                                provincia = ""
                                citta = ""
                                cap = ""
                                viewModel.updateProvincePerRegione(r)
                                expRegione = false
                            }
                        )
                    }
                }
            }
            Spacer(modifier = Modifier.height(8.dp))

            // PROVINCIA E CITTA OPERATIVITA
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                ExposedDropdownMenuBox(
                    expanded = expProvincia,
                    onExpandedChange = { if (regione.isNotEmpty()) expProvincia = !expProvincia },
                    modifier = Modifier.weight(1f)
                ) {
                    OutlinedTextField(
                        value = provincia,
                        onValueChange = {},
                        readOnly = true,
                        enabled = regione.isNotEmpty(),
                        label = { Text(stringResource(Res.string.province_label)) },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expProvincia) },
                        modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                    ExposedDropdownMenu(expanded = expProvincia, onDismissRequest = { expProvincia = false }) {
                        viewModel.listaProvince.forEach { p ->
                            DropdownMenuItem(
                                text = { Text(p) },
                                onClick = {
                                    provincia = p
                                    citta = ""
                                    cap = ""
                                    viewModel.updateComuniPerProvincia(p)
                                    expProvincia = false
                                }
                            )
                        }
                    }
                }

                ExposedDropdownMenuBox(
                    expanded = expComune,
                    onExpandedChange = { if (provincia.isNotEmpty()) expComune = !expComune },
                    modifier = Modifier.weight(2f)
                ) {
                    OutlinedTextField(
                        value = citta,
                        onValueChange = {},
                        readOnly = true,
                        enabled = provincia.isNotEmpty(),
                        label = { Text(stringResource(Res.string.city_label)) },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expComune) },
                        modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                    ExposedDropdownMenu(expanded = expComune, onDismissRequest = { expComune = false }) {
                        viewModel.listaComuni.forEach { c ->
                            DropdownMenuItem(
                                text = { Text(c) },
                                onClick = {
                                    citta = c
                                    cap = ""
                                    viewModel.updateCapPerComune(c)
                                    expComune = false
                                }
                            )
                        }
                    }
                }
            }
            Spacer(modifier = Modifier.height(8.dp))

            // CAP OPERATIVITA
            ExposedDropdownMenuBox(
                expanded = expCap,
                onExpandedChange = { if (citta.isNotEmpty()) expCap = !expCap },
                modifier = Modifier.fillMaxWidth()
            ) {
                OutlinedTextField(
                    value = cap,
                    onValueChange = {},
                    readOnly = true,
                    enabled = citta.isNotEmpty(),
                    label = { Text(stringResource(Res.string.cap_label)) },
                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expCap) },
                    modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp)
                )
                ExposedDropdownMenu(expanded = expCap, onDismissRequest = { expCap = false }) {
                    viewModel.listaCap.forEach { cp ->
                        DropdownMenuItem(
                            text = { Text(cp) },
                            onClick = {
                                cap = cp
                                expCap = false
                            }
                        )
                    }
                }
            }
            Spacer(modifier = Modifier.height(16.dp))
            OutlinedTextField(value = telefono, onValueChange = { telefono = it }, label = { Text(stringResource(Res.string.phone_label)) }, keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone), modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp))
        } else {
            OutlinedTextField(value = nomeFarmacia, onValueChange = { nomeFarmacia = it }, label = { Text(stringResource(Res.string.pharmacy_name_label) + " *") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp))
            OutlinedTextField(value = ragioneSociale, onValueChange = { ragioneSociale = it }, label = { Text("Ragione Sociale (Fiscale)") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp))
            Spacer(modifier = Modifier.height(8.dp))
            OutlinedTextField(value = partitaIva, onValueChange = { partitaIva = it }, label = { Text(stringResource(Res.string.vat_number) + " *") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp))
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                OutlinedTextField(value = codiceSDI, onValueChange = { codiceSDI = it }, label = { Text("Codice SDI") }, modifier = Modifier.weight(0.5f), shape = RoundedCornerShape(12.dp))
                OutlinedTextField(value = pec, onValueChange = { pec = it }, label = { Text("PEC") }, modifier = Modifier.weight(1f), shape = RoundedCornerShape(12.dp))
            }
            Spacer(modifier = Modifier.height(8.dp))
            OutlinedTextField(value = numeroIscrizioneOrdineDirettore, onValueChange = { numeroIscrizioneOrdineDirettore = it }, label = { Text(stringResource(Res.string.director_registration_label)) }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp))
            Spacer(modifier = Modifier.height(8.dp))
            OutlinedTextField(value = indirizzo, onValueChange = { indirizzo = it }, label = { Text(stringResource(Res.string.address_label)) }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp))
            Spacer(modifier = Modifier.height(8.dp))

            // REGIONE
            ExposedDropdownMenuBox(
                expanded = expRegione,
                onExpandedChange = { 
                    expRegione = !expRegione 
                    if (expRegione && viewModel.listaRegioni.isEmpty()) {
                        viewModel.loadRegioni()
                    }
                },
                modifier = Modifier.fillMaxWidth()
            ) {
                OutlinedTextField(
                    value = regione,
                    onValueChange = {},
                    readOnly = true,
                    label = { Text(stringResource(Res.string.region_label)) },
                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expRegione) },
                    modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp)
                )
                ExposedDropdownMenu(expanded = expRegione, onDismissRequest = { expRegione = false }) {
                    viewModel.listaRegioni.forEach { r ->
                        DropdownMenuItem(
                            text = { Text(r) },
                            onClick = {
                                regione = r
                                provincia = ""
                                citta = ""
                                cap = ""
                                viewModel.updateProvincePerRegione(r)
                                expRegione = false
                            }
                        )
                    }
                }
            }
            Spacer(modifier = Modifier.height(8.dp))

            // PROVINCIA E CITTA (COMUNE)
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                // PROVINCIA
                ExposedDropdownMenuBox(
                    expanded = expProvincia,
                    onExpandedChange = { if (regione.isNotEmpty()) expProvincia = !expProvincia },
                    modifier = Modifier.weight(1f)
                ) {
                    OutlinedTextField(
                        value = provincia,
                        onValueChange = {},
                        readOnly = true,
                        enabled = regione.isNotEmpty(),
                        label = { Text(stringResource(Res.string.province_label)) },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expProvincia) },
                        modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                    ExposedDropdownMenu(expanded = expProvincia, onDismissRequest = { expProvincia = false }) {
                        viewModel.listaProvince.forEach { p ->
                            DropdownMenuItem(
                                text = { Text(p) },
                                onClick = {
                                    provincia = p
                                    citta = ""
                                    cap = ""
                                    viewModel.updateComuniPerProvincia(p)
                                    expProvincia = false
                                }
                            )
                        }
                    }
                }

                // COMUNE (CITTA)
                ExposedDropdownMenuBox(
                    expanded = expComune,
                    onExpandedChange = { if (provincia.isNotEmpty()) expComune = !expComune },
                    modifier = Modifier.weight(2f)
                ) {
                    OutlinedTextField(
                        value = citta,
                        onValueChange = {},
                        readOnly = true,
                        enabled = provincia.isNotEmpty(),
                        label = { Text(stringResource(Res.string.city_label)) },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expComune) },
                        modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                    ExposedDropdownMenu(expanded = expComune, onDismissRequest = { expComune = false }) {
                        viewModel.listaComuni.forEach { c ->
                            DropdownMenuItem(
                                text = { Text(c) },
                                onClick = {
                                    citta = c
                                    cap = ""
                                    viewModel.updateCapPerComune(c)
                                    expComune = false
                                }
                            )
                        }
                    }
                }
            }
            Spacer(modifier = Modifier.height(8.dp))

            // CAP
            ExposedDropdownMenuBox(
                expanded = expCap,
                onExpandedChange = { if (citta.isNotEmpty()) expCap = !expCap },
                modifier = Modifier.fillMaxWidth()
            ) {
                OutlinedTextField(
                    value = cap,
                    onValueChange = {},
                    readOnly = true,
                    enabled = citta.isNotEmpty(),
                    label = { Text(stringResource(Res.string.cap_label)) },
                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expCap) },
                    modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp)
                )
                ExposedDropdownMenu(expanded = expCap, onDismissRequest = { expCap = false }) {
                    viewModel.listaCap.forEach { cp ->
                        DropdownMenuItem(
                            text = { Text(cp) },
                            onClick = {
                                cap = cp
                                expCap = false
                            }
                        )
                    }
                }
            }
            Spacer(modifier = Modifier.height(8.dp))
            OutlinedTextField(value = telefono, onValueChange = { telefono = it }, label = { Text(stringResource(Res.string.phone_label)) }, keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone), modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp))
        }

        Spacer(modifier = Modifier.height(8.dp))
        OutlinedTextField(value = email, onValueChange = { email = it }, label = { Text(stringResource(Res.string.email_label) + " *") }, keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email), modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp))
        Spacer(modifier = Modifier.height(8.dp))
        OutlinedTextField(value = password, onValueChange = { password = it }, label = { Text(stringResource(Res.string.password_label) + " *") }, visualTransformation = PasswordVisualTransformation(), modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp))

        if (viewModel.registrationSuccess) {
            AlertDialog(
                onDismissRequest = { onBackToLogin() },
                confirmButton = {
                    Button(onClick = { onBackToLogin() }) {
                        Text(stringResource(Res.string.go_to_login))
                    }
                },
                title = { Text(stringResource(Res.string.registration_success_title)) },
                text = { Text(stringResource(Res.string.registration_success_message)) }
            )
        }

        if (errorMsgLocal != null || viewModel.errorMessage != null) {
            Text(text = errorMsgLocal ?: viewModel.errorMessage!!, color = Color.Red, modifier = Modifier.padding(bottom = 8.dp))
        }

        if (!isFormValid && !viewModel.isLoading) {
            Text(
                stringResource(Res.string.mandatory_fields_notice),
                color = Color.Gray,
                style = MaterialTheme.typography.labelLarge,
                modifier = Modifier.padding(bottom = 8.dp)
            )
        }

        Button(
            onClick = {
                errorMsgLocal = null
                if (role == UserRole.FARMACISTA) {
                    val cfPuro = codiceFiscale.trim().uppercase().filter { it.isLetterOrDigit() }
                    if (cfPuro.length != 16) {
                        errorMsgLocal = taxCodeErrorLength
                        return@Button
                    }
                    if (!ItalianCfUtils.isCodiceFiscaleValido(cfPuro)) {
                        errorMsgLocal = taxCodeErrorInvalid
                        return@Button
                    }
                    
                        viewModel.registerFarmacista(Farmacista(
                            nome = nome.trim(), cognome = cognome.trim(), sesso = sesso, 
                            partitaIva = partitaIva.trim(), codiceFiscale = cfPuro, 
                            numeroIscrizioneOrdine = numeroIscrizioneOrdine.trim(),
                            luogoNascita = luogoNascita.trim(), 
                            cittadinanza = cittadinanza.trim(), email = email.trim().lowercase(),
                            telefono = telefono.trim(), password = password.trim(),
                            regimeFiscale = regimeFiscale,
                            percEnpaf = percEnpaf.toDoubleOrNull() ?: 2.0,
                            regioneOperativita = regione, provinciaOperativita = provincia,
                            comuneOperativita = citta, capOperativita = cap,
                            competenze = selectedSkills.toList()
                        ))
                } else {
                    viewModel.registerFarmacia(Farmacia(
                        nomeFarmacia = nomeFarmacia.trim(), ragioneSociale = ragioneSociale.trim(),
                        partitaIva = partitaIva.trim(), codiceSDI = codiceSDI.trim(), pec = pec.trim(),
                        numeroIscrizioneOrdineDirettore = numeroIscrizioneOrdineDirettore.trim(),
                        indirizzo = indirizzo.trim(), cap = cap.trim(), citta = citta.trim(), 
                        provincia = provincia.trim(), regione = regione.trim(), 
                        telefono = telefono.trim(), email = email.trim().lowercase(), password = password.trim()
                    ))
                }
            },
            modifier = Modifier.fillMaxWidth().height(56.dp),
            enabled = isFormValid && !viewModel.isLoading,
            shape = RoundedCornerShape(12.dp)
        ) {
            if (viewModel.isLoading) {
                CircularProgressIndicator(color = Color.White, modifier = Modifier.size(24.dp))
            } else {
                Text(stringResource(Res.string.register_button_caps), fontWeight = FontWeight.Bold)
            }
        }

        TextButton(onClick = onBackToLogin) {
            Text(stringResource(Res.string.already_have_account_button))
        }
    }
}
